import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  ChartBarIcon, 
  TrophyIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import EmployeePerformanceService from '../services/employeePerformanceService';

const EmployeePerformanceDashboard = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadPerformanceData();
  }, []);

  const loadPerformanceData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await EmployeePerformanceService.getPerformanceAnalytics();
      
      if (result.success) {
        setPerformanceData(result.data);
      } else {
        setError(result.error || 'Failed to load performance data');
      }
    } catch (error) {
      console.error('Error loading performance data:', error);
      setError('Failed to load performance data');
    }
    
    setLoading(false);
  };

  const handleEmployeeSelect = async (employee) => {
    setLoading(true);
    
    try {
      const result = await EmployeePerformanceService.getEmployeeDetails(employee._id);
      
      if (result.success) {
        setSelectedEmployee(result.data);
        setActiveTab('profile');
      } else {
        setError(result.error || 'Failed to load employee details');
      }
    } catch (error) {
      console.error('Error loading employee details:', error);
      setError('Failed to load employee details');
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
        <button 
          onClick={loadPerformanceData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Employee Performance Dashboard</h1>
        <p className="text-blue-100">Comprehensive analytics and insights for your sales team</p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Team Overview
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'performance'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Performance Metrics
          </button>
          {selectedEmployee && (
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Employee Profile
            </button>
          )}
        </nav>
      </div>

      {/* Team Overview Tab */}
      {activeTab === 'overview' && performanceData && (
        <div className="space-y-6">
          {/* Team Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <UserGroupIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Employees</p>
                  <p className="text-2xl font-bold text-gray-900">{performanceData?.summary?.totalEmployees || performanceData?.teamStatistics?.totalEmployees || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ChartBarIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Leads</p>
                  <p className="text-2xl font-bold text-gray-900">{performanceData?.summary?.totalRevenue || performanceData?.teamStatistics?.totalLeads || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <TrophyIcon className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Conversions</p>
                  <p className="text-2xl font-bold text-gray-900">{performanceData?.teamStatistics?.totalConversions || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {EmployeePerformanceService.formatCurrency(performanceData?.summary?.totalRevenue || performanceData?.teamStatistics?.totalRevenue || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Performance Grid */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Employee Performance Overview</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Workload
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conversions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Follow-ups
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(performanceData?.employees || []).map((emp, index) => (
                    <tr key={emp.employee?._id || emp._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {(emp.employee?.name || emp.name || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{emp.employee?.name || emp.name || 'Unknown'}</div>
                            <div className="text-sm text-gray-500">{emp.employee?.role || emp.role || 'Employee'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{emp.workload?.totalLeads || emp.totalLeads || 0} leads</div>
                        <div className="text-sm text-gray-500">
                          {EmployeePerformanceService.getWorkloadLevel(emp.workload?.workloadPercentage || 0).level}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{emp.conversionMetrics?.convertedLeads || emp.convertedLeads || 0}</div>
                        <div className="text-sm text-gray-500">
                          {EmployeePerformanceService.formatPercentage(emp.conversionMetrics?.conversionRate || emp.conversionRate || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{emp.followUpMetrics?.completed || 0}/{emp.followUpMetrics?.total || 0}</div>
                        <div className="text-sm text-gray-500">
                          {EmployeePerformanceService.formatPercentage(emp.followUpMetrics?.completionRate || emp.conversionRate || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          EmployeePerformanceService.getPerformanceColor(emp.performance?.score || emp.performanceScore || 0)
                        }`}>
                          {emp.performance?.score || emp.performanceScore || 0}% - {emp.performance?.rating || 'Good'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEmployeeSelect(emp.employee || emp)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Performance Metrics Tab */}
      {activeTab === 'performance' && performanceData && (
        <div className="space-y-6">
          {/* Top Performers */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">🏆 Top Performers</h3>
              <div className="space-y-3">
                {(performanceData?.employees || []).slice(0, 5).map((emp, index) => (
                  <div key={emp.employee?._id || emp._id || index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                      <span className="text-sm font-medium text-gray-900 ml-2">{emp.employee?.name || emp.name || 'Unknown'}</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">{emp.performance?.score || emp.performanceScore || 0}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">💰 Revenue Leaders</h3>
              <div className="space-y-3">
                {(performanceData?.employees || [])
                  .sort((a, b) => (b.leadStatistics?.convertedValue || 0) - (a.leadStatistics?.convertedValue || 0))
                  .slice(0, 5)
                  .map((emp, index) => (
                    <div key={emp.employee?._id || emp._id || index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                        <span className="text-sm font-medium text-gray-900 ml-2">{emp.employee?.name || emp.name || 'Unknown'}</span>
                      </div>
                      <span className="text-sm font-bold text-purple-600">
                        {EmployeePerformanceService.formatCurrency(emp.leadStatistics?.convertedValue || emp.totalValue || 0)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">📞 Follow-up Champions</h3>
              <div className="space-y-3">
                {(performanceData?.employees || [])
                  .sort((a, b) => (b.followUpMetrics?.completionRate || 0) - (a.followUpMetrics?.completionRate || 0))
                  .slice(0, 5)
                  .map((emp, index) => (
                    <div key={emp.employee?._id || emp._id || index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                        <span className="text-sm font-medium text-gray-900 ml-2">{emp.employee?.name || emp.name || 'Unknown'}</span>
                      </div>
                      <span className="text-sm font-bold text-blue-600">
                        {EmployeePerformanceService.formatPercentage(emp.followUpMetrics?.completionRate || emp.conversionRate || 0)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Employee Profile Tab */}
      {activeTab === 'profile' && selectedEmployee && (
        <div className="space-y-6">
          {/* Employee Header */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-xl font-bold text-gray-700">
                    {selectedEmployee.employee.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="ml-6">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.employee.name}</h2>
                  <p className="text-gray-600">{selectedEmployee.employee.role} • {selectedEmployee.employee.department}</p>
                  <div className="flex items-center mt-2 space-x-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      {selectedEmployee.employee.email}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <PhoneIcon className="h-4 w-4 mr-1" />
                      {selectedEmployee.employee.phone}
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('overview')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back to Overview
              </button>
            </div>
          </div>

          {/* Employee Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ChartBarIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Leads</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedEmployee?.leads?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Conversions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedEmployee?.leads?.filter(lead => lead.status === 'closed_won').length || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Follow-ups</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedEmployee?.leads?.reduce((sum, lead) => sum + (lead.followUpsCount || 0), 0) || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {EmployeePerformanceService.formatCurrency(
                      selectedEmployee?.leads
                        ?.filter(lead => lead.status === 'closed_won')
                        ?.reduce((sum, lead) => sum + (lead.value || 0), 0) || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Leads */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Assigned Leads</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Follow-ups
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(selectedEmployee?.leads || []).slice(0, 10).map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                          <div className="text-sm text-gray-500">{lead.company}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          EmployeePerformanceService.getStatusColor(lead.status)
                        }`}>
                          {lead.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {EmployeePerformanceService.formatCurrency(lead.value || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.followUpsCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {EmployeePerformanceService.formatDate(lead.assignedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePerformanceDashboard;
